import Modal from './modal.js';
import { createDebugger } from '../utils/debug.js';
import { validateNorwegianOrgNumber } from '../utils/norwegian-validation.js';

const debug = createDebugger('org-number-modal', false);

export default class OrgNumberModal extends Modal {
    constructor() {
        super();
        debug.log('Constructor called');
        this.orgNumber = null;
        this.settings = { target_countries: ['NO'] }; // Initialize with defaults
        this.errorTimeout = null; // Track error message timeout
        this.formListenerAdded = false; // Track if form listener is already added
    }

    connectedCallback() {
        debug.log('connectedCallback called');
        super.connectedCallback();
        this.init();
    }

    async init() {
        debug.log('init() called');
        try {
            // Get settings from data attribute
            this.settings = this.getSettings();
            debug.log('Settings loaded:', this.settings);

            // Check if modal should auto-open (determined by Liquid based on cart attributes)
            const shouldAutoOpen = this.dataset.shouldAutoOpen === 'true';
            debug.log('Should auto-open (from Liquid):', shouldAutoOpen);
            debug.log('Has org number (from Liquid):', this.settings.has_org_number);
            debug.log('Current country (from Liquid):', this.settings.current_country);

            // Setup modal (always setup for manual triggering)
            this.setupModal();

            // Setup global toggle button listeners
            this.setupToggleListeners();

            // Auto-open if Liquid determined we should (checks cart attributes for org number and skip flag)
            if (shouldAutoOpen) {
                debug.log('Auto-opening modal - user in target country without org number');
                this.autoOpen();
            } else {
                debug.log('Not auto-opening - either has org number, not in target country, or user skipped');
            }
        } catch (error) {
            debug.error('Error initializing org number modal:', error);
        }
    }

    setupToggleListeners() {
        debug.log('Setting up toggle button listeners');
        // Listen for clicks on any element with data-toggle-org-number-modal
        document.addEventListener('click', (e) => {
            const toggleButton = e.target.closest('[data-toggle-org-number-modal]');
            if (toggleButton) {
                e.preventDefault();
                debug.log('Toggle button clicked, opening modal');
                this.open();
            }
        });
    }

    getSettings() {
        const settingsElement = this.querySelector('[data-org-number-settings]');
        if (!settingsElement) {
            debug.warn('No settings element found');
            return {};
        }

        try {
            return JSON.parse(settingsElement.textContent);
        } catch (error) {
            debug.error('Error parsing settings:', error);
            return {};
        }
    }

    setupModal() {
        debug.log('setupModal() called');
        // Render modal content
        this.renderContent();

        // Setup form submission (only once)
        if (!this.formListenerAdded) {
            const form = this.querySelector('[data-org-number-form]');
            debug.log('Form element:', form);
            if (form) {
                form.addEventListener('submit', (e) => this.handleSubmit(e));
                this.formListenerAdded = true;
            }

            // Setup skip button
            const skipButton = this.querySelector('[data-skip-button]');
            if (skipButton) {
                skipButton.addEventListener('click', () => this.handleSkip());
            }
        }

        debug.log('setupModal() completed');
    }

    renderContent() {
        debug.log('renderContent() called');
        const contentArea = this.querySelector('.modal__body');
        debug.log('Content area:', contentArea);
        if (!contentArea) {
            debug.error('No .modal__body found!');
            return;
        }

        const template = this.querySelector('[data-content-template]');
        debug.log('Template:', template);
        if (!template) {
            debug.error('No content template found');
            return;
        }

        contentArea.innerHTML = template.innerHTML;
        debug.log('Content rendered, innerHTML length:', contentArea.innerHTML.length);
    }

    async handleSubmit(e) {
        e.preventDefault();

        const form = e.target;
        const orgNumberInput = form.querySelector('[data-org-number-input]');
        const orgNumber = orgNumberInput?.value.trim();

        if (!orgNumber) {
            this.showError(this.settings.error_message || 'Please enter an organization number');
            return;
        }

        // Remove spaces for length check
        const cleaned = orgNumber.replace(/[\s.-]/g, '');

        // Check if it's 9 digits first
        if (!/^\d{9}$/.test(cleaned)) {
            this.showError(this.settings.invalid_format_message || 'Organization number must be 9 digits');
            return;
        }

        // Validate org number using MOD11 algorithm
        if (!validateNorwegianOrgNumber(cleaned)) {
            this.showError(this.settings.invalid_number_message || 'Invalid organization number');
            return;
        }

        debug.log('Submitting org number:', cleaned);

        // Set loading state
        this.setLoadingState(true);

        try {
            // Save org number to cart attributes (use cleaned version for consistency)
            await this.saveOrgNumber(cleaned);

            // Show success message
            this.showSuccess(this.settings.success_message || 'Organization number saved successfully');

            // Update orgNumber so modal can be closed
            this.orgNumber = cleaned;

            // Close modal after short delay
            setTimeout(() => {
                this.close();
            }, 1000);
        } catch (error) {
            debug.error('Error saving org number:', error);
            this.showError(this.settings.save_error_message || 'Failed to save organization number');
        } finally {
            // Remove loading state
            this.setLoadingState(false);
        }
    }

    setLoadingState(isLoading) {
        const submitButton = this.querySelector('[data-submit-button]');
        const buttonText = this.querySelector('[data-button-text]');
        const buttonLoading = this.querySelector('[data-button-loading]');

        if (!submitButton || !buttonText || !buttonLoading) {
            return;
        }

        if (isLoading) {
            submitButton.disabled = true;
            buttonText.classList.add('hidden');
            buttonLoading.classList.remove('hidden');
        } else {
            submitButton.disabled = false;
            buttonText.classList.remove('hidden');
            buttonLoading.classList.add('hidden');
        }
    }

    async saveOrgNumber(orgNumber) {
        const formData = new FormData();
        formData.append('attributes[org_number]', orgNumber);
        // Clear skip flag when entering org number
        formData.append('attributes[org_number_skipped]', '');

        const response = await fetch('/cart/update.js', {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error('Failed to update cart');
        }

        const cart = await response.json();
        debug.log('Cart updated:', cart);

        return cart;
    }

    async handleSkip() {
        debug.log('User skipped - not buying as a business');

        try {
            // Save skip flag to cart attributes
            await this.saveSkipFlag();
            // Close the modal
            this.close();
        } catch (error) {
            debug.error('Error saving skip flag:', error);
            // Still close the modal even if save fails
            this.close();
        }
    }

    async saveSkipFlag() {
        const formData = new FormData();
        formData.append('attributes[org_number_skipped]', 'yes');

        const response = await fetch('/cart/update.js', {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error('Failed to update cart');
        }

        const cart = await response.json();
        debug.log('Skip flag saved to cart:', cart);

        return cart;
    }

    canClose() {
        // Modal can always be closed now
        return true;
    }

    shouldCloseOnOutsideClick() {
        // Allow closing by clicking outside
        return true;
    }

    onOpen() {
        super.onOpen();
        // Modal is now open and can be closed freely
    }

    handleKeyUp(evt) {
        // Allow escape key to close
        if (evt.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    autoOpen() {
        debug.log('autoOpen() called');
        // Small delay to ensure page is fully loaded and focus trap is ready
        setTimeout(() => {
            debug.log('Opening modal automatically');
            this.open();
        }, 500);
    }

    showError(message) {
        // Hide success message first
        const successElement = this.querySelector('[data-success-message]');
        if (successElement) {
            successElement.classList.add('hidden');
        }

        const errorElement = this.querySelector('[data-error-message]');
        if (errorElement) {
            errorElement.textContent = message;
            errorElement.classList.remove('hidden');

            // Clear previous timeout to prevent stacking
            if (this.errorTimeout) {
                clearTimeout(this.errorTimeout);
            }

            // Auto-hide after 5 seconds
            this.errorTimeout = setTimeout(() => {
                errorElement.classList.add('hidden');
                this.errorTimeout = null;
            }, 5000);
        }
    }

    showSuccess(message) {
        // Hide error message first
        const errorElement = this.querySelector('[data-error-message]');
        if (errorElement) {
            errorElement.classList.add('hidden');
        }

        // Clear error timeout since we're showing success
        if (this.errorTimeout) {
            clearTimeout(this.errorTimeout);
            this.errorTimeout = null;
        }

        const successElement = this.querySelector('[data-success-message]');
        if (successElement) {
            successElement.textContent = message;
            successElement.classList.remove('hidden');
        }
    }
}

customElements.define('org-number-modal', OrgNumberModal);
debug.log('OrgNumberModal component registered');
